import { ReactComponent as EllipsisIcon } from '@assets/components/panel/notification/ellipsis.svg';
import { IcCopyPaste, IcEdit, IcTrash } from '@assets/icons/24x24';
import { Box, ButtonGroup, Center, For, Grid, GridItem, HStack, Icon, IconButton, Text } from '@chakra-ui/react';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import useListFormatter from '@hooks/useListFormatter';
import { hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { PlatformSelectors } from '@redux/config/config.selectors';
import { RootState } from '@redux/store';
import { getPlatformsCodesFromMarks } from '@utils/common.utils';
import { sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';
import {
  getDuplicatedConfiguration,
  isEditableSensor,
  isHostConfiguration,
  isSensorConfiguration,
} from '@utils/sensors/sensors.utils';
import { FormattedMessage } from 'react-intl';

import { ActionEnum } from '@/types/commons/commons.types';
import { SensorInstallationStatusEnum } from '@/types/filters.types';
import { SensorOrSubSensorWithShorthand } from '@/types/sensor/sensorInstallation.types';
import { HostConfiguration, PortConfiguration } from '@/types/sensor/sensors';

import StatusBar from '../../../common/StatusBar';
import { useSensorInstallationContext } from './SensorInstallationContext';

interface Props {
  sensor: SensorOrSubSensorWithShorthand;
  setOpen: (open: boolean) => void;
}

export default function SensorCard({ sensor, setOpen }: Readonly<Props>) {
  const formatList = useListFormatter();
  const { setSensorToDelete, setEditedSensor } = useSensorInstallationContext();
  const platforms = useAppSelector(PlatformSelectors.selectAllPlatforms);
  const sensorPlatformCodes = isSensorConfiguration(sensor) ? getPlatformsCodesFromMarks(sensor.marks) : [];
  const sensorPlatforms = platforms.filter((platform) => sensorPlatformCodes.includes(platform.code));
  const hasMorePlatform = sensorPlatforms.length > 1;
  const hasEditSensorRole = useAppSelector((state: RootState) =>
    hasSensorRoleFromActiveProfile(state, {
      action: ActionEnum.EDIT,
      sensorFamily: sensorFamilyFromSensorType[sensor.type],
    }),
  );
  const hasCreateSensorRole = useAppSelector((state: RootState) =>
    hasSensorRoleFromActiveProfile(state, {
      action: ActionEnum.CREATE,
      sensorFamily: sensorFamilyFromSensorType[sensor.type],
    }),
  );
  const hasDeleteSensorRole = useAppSelector((state: RootState) =>
    hasSensorRoleFromActiveProfile(state, {
      action: ActionEnum.DELETE,
      sensorFamily: sensorFamilyFromSensorType[sensor.type],
    }),
  );

  const handleDelete = () => {
    setSensorToDelete({ id: sensor.id, site: sensor.site, appCode: sensor.appCode, name: sensor.name });
    setOpen(true);
  };

  return (
    <Grid
      templateColumns="3fr 2fr 3fr 1fr 2fr 2fr"
      width="100%"
      height="56px"
      paddingLeft={2}
      gap={2}
      backgroundColor="neutral.800"
      _hover={{ backgroundColor: 'neutral.700' }}
    >
      <GridItem display="flex" alignItems="center" gap={2}>
        <Box flexShrink={0}>
          <StatusBar activated />
        </Box>
        <Tooltip content={sensor.name} openDelay={300}>
          <Text
            backgroundColor="neutral.black"
            paddingX={0.5}
            color="neutral.white"
            lineClamp={1}
            wordBreak="break-all"
          >
            {sensor.name}
          </Text>
        </Tooltip>
      </GridItem>
      <GridItem display="flex" alignItems="center">
        <HStack gap={0.25} width="100%">
          <For each={sensorPlatforms.slice(0, 1)}>
            {(platform) => (
              <Text key={platform.code} color="neutral.white" backgroundColor="neutral.600" paddingX={0.5}>
                {platform.name}
              </Text>
            )}
          </For>
          {hasMorePlatform && (
            <Tooltip content={formatList(sensorPlatforms.map((platform) => platform.name).slice(1))} openDelay={300}>
              <Center width="15px" height="17.5px" backgroundColor="neutral.600" paddingTop="11px">
                <Icon as={EllipsisIcon} width="8px" height="3px" color="neutral.white" />
              </Center>
            </Tooltip>
          )}
        </HStack>
      </GridItem>
      <GridItem display="flex" alignItems="center" gap={1}>
        {isSensorConfiguration(sensor) && isHostConfiguration(sensor) && (sensor as HostConfiguration).host ? (
          <>
            <Text color="neutral.300">IP</Text>
            <Text color="neutral.white">{(sensor as HostConfiguration).host}</Text>
          </>
        ) : (
          <Text visibility="hidden">-</Text> // Keeps spacing consistent
        )}
      </GridItem>
      <GridItem display="flex" alignItems="center" gap={1}>
        {isSensorConfiguration(sensor) && isHostConfiguration(sensor) && (sensor as PortConfiguration).port ? (
          <>
            <Text color="neutral.300">Port</Text>
            <Text color="neutral.white">{(sensor as PortConfiguration).port}</Text>
          </>
        ) : (
          <Text visibility="hidden">-</Text> // Keeps spacing consistent
        )}
      </GridItem>
      <GridItem display="flex" alignItems="center">
        <Text color="green" paddingX={0.5} backgroundColor="neutral.900">
          <FormattedMessage
            id={`components.sensorInstallation.filters.sensors.sensorInstallationStatus.${SensorInstallationStatusEnum.OPERATIONAL}`}
          />
        </Text>
      </GridItem>
      {isEditableSensor(sensor) && (
        <HStack justifyContent="flex-end">
          <ButtonGroup attached height="100%" borderLeft="2px solid" borderColor="neutral.900">
            {hasEditSensorRole && (
              <IconButton
                variant="transparent"
                boxSize="56px"
                aria-label="edit"
                onClick={() => setEditedSensor(sensor)}
              >
                <Icon as={IcEdit} boxSize="24px" />
              </IconButton>
            )}
            {hasCreateSensorRole && (
              <IconButton
                variant="transparent"
                boxSize="56px"
                aria-label="duplicate"
                onClick={() => setEditedSensor(getDuplicatedConfiguration(sensor))}
              >
                <Icon as={IcCopyPaste} boxSize="24px" />
              </IconButton>
            )}
            {hasDeleteSensorRole && (
              <IconButton variant="transparent" boxSize="56px" aria-label="delete" onClick={handleDelete}>
                <Icon as={IcTrash} boxSize="24px" />
              </IconButton>
            )}
          </ButtonGroup>
        </HStack>
      )}
    </Grid>
  );
}
